export type TextSelection = { start: number; end: number };

const isLowSurrogate = (code: number) => code >= 0xdc00 && code <= 0xdfff;

/**
 * Nudge an offset forward so it never lands between the two UTF-16 halves of
 * a surrogate pair — splicing there would corrupt the existing emoji.
 */
const snapToCodePoint = (text: string, index: number) =>
  index > 0 && index < text.length && isLowSurrogate(text.charCodeAt(index))
    ? index + 1
    : index;

/**
 * Insert `insert` into `text` at the given selection, replacing any selected
 * range. Without a known selection the insert is appended, matching the
 * previous behaviour. Returns the new text and the caret offset just after
 * the inserted string.
 */
export const insertAtSelection = (
  text: string,
  insert: string,
  selection?: TextSelection | null
): { text: string; caret: number } => {
  if (!selection) {
    return { text: text + insert, caret: text.length + insert.length };
  }
  const clamp = (n: number) => Math.min(Math.max(n, 0), text.length);
  const a = snapToCodePoint(text, clamp(selection.start));
  const b = snapToCodePoint(text, clamp(selection.end));
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return {
    text: text.slice(0, start) + insert + text.slice(end),
    caret: start + insert.length,
  };
};
